import Sortable from "../vendor/sortable.esm"
import { showBackofficeFlash } from "packs/init-backoffice-flashes"
import { initBackofficeFileInputs } from "packs/init-backoffice-file-inputs"
import { confirmBackofficeDelete } from "packs/init-backoffice-confirm"
import { initBackofficeUnsavedWarn, markBackofficeFormClean } from "packs/init-backoffice-unsaved-warn"

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const logosCountLabel = (count) =>
  count > 1 ? `(${count} logos)` : `(${count} logo)`

const syncLogosUi = (root) => {
  if (!root) return

  const grid = root.querySelector("[data-partner-logos]")
  const empty = root.querySelector("[data-partner-logos-empty]")
  const hint = root.querySelector("[data-partner-logos-hint]")
  const count = grid?.querySelectorAll("[data-partner-logo]").length || 0

  if (empty) empty.hidden = count > 0
  if (grid) grid.hidden = count === 0
  if (hint) hint.hidden = count <= 1

  const group = root.closest(".backoffice-content-group")
  const countEl = group?.querySelector("[data-content-docs-count]")
  if (countEl) countEl.textContent = logosCountLabel(count)
}

const partnerLogoCard = (doc) => {
  const figure = document.createElement("figure")
  figure.className = "backoffice-photo-card backoffice-partner-logo-card"
  figure.dataset.partnerLogo = "true"
  figure.dataset.partnerLogoId = String(doc.id)
  figure.dataset.contentDocumentId = String(doc.id)

  const handle = document.createElement("span")
  handle.className = "backoffice-faq-handle backoffice-partner-logo-handle"
  handle.dataset.partnerLogoHandle = "true"
  handle.title = "Réordonner"
  handle.setAttribute("aria-label", "Réordonner le logo")
  figure.appendChild(handle)

  if (doc.url) {
    const link = document.createElement("a")
    link.href = doc.preview_url || doc.url
    link.target = "_blank"
    link.rel = "noopener"
    const img = document.createElement("img")
    img.src = doc.url
    img.alt = doc.title || ""
    img.draggable = false
    link.appendChild(img)
    figure.appendChild(link)
  }

  const caption = document.createElement("figcaption")

  const rename = document.createElement("form")
  rename.className = "backoffice-photo-card__rename"
  rename.action = doc.update_url
  rename.method = "post"
  rename.dataset.partnerLogoRename = "true"

  const method = document.createElement("input")
  method.type = "hidden"
  method.name = "_method"
  method.value = "patch"
  rename.appendChild(method)

  const token = document.createElement("input")
  token.type = "hidden"
  token.name = "authenticity_token"
  token.value = csrfToken() || ""
  rename.appendChild(token)

  const titleInput = document.createElement("input")
  titleInput.type = "text"
  titleInput.name = "document[title]"
  titleInput.value = doc.title || ""
  titleInput.className = "backoffice-input"
  titleInput.required = true
  titleInput.setAttribute("aria-label", "Nom du logo")
  rename.appendChild(titleInput)

  const ok = document.createElement("input")
  ok.type = "submit"
  ok.value = "OK"
  ok.className = "backoffice-btn backoffice-btn--ghost"
  rename.appendChild(ok)

  caption.appendChild(rename)
  bindPartnerLogoRenameForm(rename)

  const destroy = document.createElement("a")
  destroy.href = doc.destroy_url
  destroy.className = "backoffice-btn backoffice-btn--danger"
  destroy.dataset.partnerLogoDelete = "true"
  destroy.dataset.confirm = "Supprimer ce logo ?"
  destroy.textContent = "Supprimer"
  caption.appendChild(destroy)

  figure.appendChild(caption)
  return figure
}

const upsertPartnerLogo = (root, doc) => {
  if (!root || !doc) return

  const grid = root.querySelector("[data-partner-logos]")
  if (!grid) return

  const existing = grid.querySelector(`[data-partner-logo-id="${doc.id}"]`)
  const card = partnerLogoCard(doc)
  if (existing) existing.replaceWith(card)
  else grid.appendChild(card)

  syncLogosUi(root)
}

const removePartnerLogo = (root, documentId) => {
  if (!root || !documentId) return

  root
    .querySelector(`[data-partner-logo-id="${documentId}"]`)
    ?.remove()
  syncLogosUi(root)
}

const resetPartnerLogoForm = (form) => {
  form.reset()
  form.querySelectorAll(".backoffice-file").forEach((wrap) => {
    const name = wrap.querySelector(".backoffice-file__name")
    if (name) {
      name.textContent = "Aucun fichier choisi"
      name.classList.remove("is-filled")
    }
  })
  form.querySelectorAll('input[type="file"]').forEach((input) => {
    input.value = ""
  })
}

const patchReorder = async (grid, ids) => {
  const url = grid.dataset.reorderUrl
  const section = grid.dataset.reorderSection
  if (!url || !section || ids.length === 0) return true

  const body = new FormData()
  ids.forEach((id) => body.append("document_ids[]", id))
  body.append("section", section)
  body.append("_method", "patch")

  const response = await fetch(url, {
    method: "POST",
    body,
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrfToken() || "",
    },
    credentials: "same-origin",
  })

  return response.ok
}

const bindPartnerLogosSortable = (grid) => {
  if (!grid || grid.dataset.sortableBound === "true") return
  grid.dataset.sortableBound = "true"

  Sortable.create(grid, {
    animation: 150,
    handle: "[data-partner-logo-handle]",
    draggable: "[data-partner-logo]",
    ghostClass: "is-dragging",
    onEnd: async () => {
      const ids = Array.from(grid.querySelectorAll("[data-partner-logo]"))
        .map((item) => item.dataset.partnerLogoId)
        .filter(Boolean)
      const ok = await patchReorder(grid, ids)
      if (!ok) showBackofficeFlash("Impossible de réordonner les logos", "alert")
    },
  })
}

const bindPartnerLogoForm = (form) => {
  if (!form || form.dataset.partnerLogosBound === "true") return
  form.dataset.partnerLogosBound = "true"

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    const root = form.closest("[data-partner-logos-root]")
    const submit = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submit) submit.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
        return
      }

      upsertPartnerLogo(root, data.document)
      resetPartnerLogoForm(form)
      initBackofficeFileInputs()
      initBackofficeUnsavedWarn()
      showBackofficeFlash(data.notice || "Logo ajouté.", "notice")
    } catch (_) {
      showBackofficeFlash("Erreur d'enregistrement", "alert")
    } finally {
      if (submit) {
        submit.disabled = false
        submit.removeAttribute("disabled")
      }
    }
  })
}

const bindPartnerLogoRenameForm = (form) => {
  if (!form || form.dataset.partnerLogoRenameBound === "true") return
  form.dataset.partnerLogoRenameBound = "true"

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    const submit = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submit) submit.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
        return
      }

      const titleInput = form.querySelector('input[name="document[title]"]')
      if (titleInput && data.document?.title != null) {
        titleInput.value = data.document.title
      }
      const img = form.closest("[data-partner-logo]")?.querySelector("img")
      if (img && data.document?.title) img.alt = data.document.title

      markBackofficeFormClean(form)
      showBackofficeFlash(data.notice || "Nom mis à jour.", "notice")
    } catch (_) {
      showBackofficeFlash("Erreur d'enregistrement", "alert")
    } finally {
      if (submit) {
        submit.disabled = false
        submit.removeAttribute("disabled")
      }
    }
  })
}

const bindPartnerLogoDeletes = () => {
  document.querySelectorAll(".backoffice-body").forEach((body) => {
    if (body.dataset.partnerLogosDeleteBound === "true") return
    body.dataset.partnerLogosDeleteBound = "true"

    body.addEventListener("click", async (event) => {
      const link = event.target.closest("[data-partner-logo-delete]")
      if (!link) return

      event.preventDefault()
      event.stopPropagation()

      const confirmed = await confirmBackofficeDelete({
        title: "Supprimer ce logo ?",
        text: link.dataset.confirm || "Le logo sera définitivement supprimé.",
      })
      if (!confirmed) return

      const root = link.closest("[data-partner-logos-root]")
      const item = link.closest("[data-partner-logo-id]")
      const documentId = item?.dataset.partnerLogoId

      try {
        const response = await fetch(link.href, {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": csrfToken() || "",
          },
          credentials: "same-origin",
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showBackofficeFlash(data.error || "Erreur de suppression", "alert")
          return
        }

        removePartnerLogo(root, documentId)
        showBackofficeFlash(data.notice || "Logo supprimé.", "notice")
      } catch (_) {
        showBackofficeFlash("Erreur de suppression", "alert")
      }
    })
  })
}

export const initBackofficePartnerLogos = () => {
  document.querySelectorAll("[data-partner-logos]").forEach(bindPartnerLogosSortable)
  document.querySelectorAll("form[data-partner-logos-form]").forEach(bindPartnerLogoForm)
  document.querySelectorAll("form[data-partner-logo-rename]").forEach(bindPartnerLogoRenameForm)
  bindPartnerLogoDeletes()
}
